import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, Router, UrlTree } from '@angular/router';
import { Observable, of } from 'rxjs';
import { JwtProcessingService } from '../../services/jwt-processing/jwt-processing.service';

/**
 * Authentication Guard for Financial Webview Operations
 *
 * @description
 * Validates JWT tokens from query parameters to secure financial operations.
 * Critical for preventing unauthorized access to real money transactions.
 * Uses JwtProcessingService for clean, centralized JWT validation.
 *
 * @businessFlow
 * 1. Extract token & lang from URL query params
 * 2. Use JwtProcessingService to validate token format, payload, and expiration
 * 3. Allow access OR redirect to access-denied with error type
 *
 * **Entry Points:** Flutter app, external systems, deep links
 * **Protects:** Wallet operations, OTP verification, PIN setup
 * **Errors:** Missing/invalid token → type=invalid, Expired → type=expired
 *
 * @param route - Contains query parameters (token, lang)
 * @returns true for valid token, UrlTree redirect for errors
 *
 * @example
 * canActivate: [appRedirectGuard]
 * URL: /wallet-otp?token=eyJ...&lang=en
 * Error: /access-denied?type=expired&lang=en
 */
export const appRedirectGuard = (route: ActivatedRouteSnapshot): Observable<boolean | UrlTree> => {
    const lang = route.queryParams['lang'] || 'id';
    const token = route.queryParams['token'];
    const router = inject(Router);
    const jwtService = inject(JwtProcessingService);

    // Check if token exists
    if (!token) {
        return of(
            router.createUrlTree(['access-denied'], {
                queryParams: { lang, type: 'invalid' },
            }),
        );
    }

    // Parse and validate JWT payload using service
    const payload = jwtService.parseJwtPayload(token);

    // Check if payload is valid (format and required fields)
    if (!jwtService.isValidPayload(payload)) {
        return of(
            router.createUrlTree(['access-denied'], {
                queryParams: { lang, type: 'invalid' },
            }),
        );
    }

    // Check if token is expired
    if (jwtService.isTokenExpired(payload)) {
        return of(
            router.createUrlTree(['access-denied'], {
                queryParams: { lang, type: 'expired' },
            }),
        );
    }

    // Token is valid
    return of(true);
};
